import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import StatsBanner from "@/components/StatsBanner";
import { DIRECTORS, COMPANY } from "@/data/content";
import { Target, Eye, Compass } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About VPCL Infra Limited"
        title="A renewable infrastructure company built for scale."
        subtitle="We design, build and operate utility-scale renewable assets for India's most demanding clients — DISCOMs, PSUs, industrial offtakers and infrastructure developers."
        image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="about-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline mb-4">Our Story</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-6 leading-tight">
              Engineered for India's energy transition.
            </h2>
            <div className="space-y-5 text-[#4A5568] leading-relaxed">
              <p>
                VPCL Infra Limited was founded with a singular focus: deliver utility-scale renewable infrastructure that meets investor-grade standards on safety, schedule and long-term performance.
              </p>
              <p>
                With multi-state execution capability, an in-house design & engineering team, and deep familiarity with central and state tender ecosystems, VPCL Infra bridges the gap between policy ambition and on-ground execution.
              </p>
              <p>
                We work across the entire value chain — from land aggregation and DPR preparation to financial closure, EPC execution and long-term O&M — making us a complete development partner.
              </p>
            </div>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1629726797843-618688139f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Solar park drone view"
              className="w-full h-[480px] object-cover"
            />
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-corp">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Mission", body: "Deliver bankable renewable infrastructure at utility scale with uncompromising HSE and schedule discipline." },
              { icon: Eye, title: "Vision", body: "To be India's most reliable renewable EPC and developer partner for government and industrial clients." },
              { icon: Compass, title: "Values", body: "Engineering rigour. Financial discipline. Long-term partnerships. Zero shortcuts on safety or quality." },
            ].map((b) => (
              <div key={b.title} className="card-corp" data-testid={`mvv-${b.title}`}>
                <b.icon className="text-[#16A34A] mb-5" size={32} strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-semibold text-[#0B2341] mb-3">{b.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <StatsBanner dark />

      <section className="section-padding bg-white">
        <div className="container-corp">
          <div className="overline mb-4">Leadership</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-14 max-w-2xl">
            Board of Directors
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#E2E8F0]">
            {DIRECTORS.map((d, i) => (
              <div key={i} className="bg-white p-8" data-testid={`director-${i}`}>
                <div className="aspect-[4/5] bg-[#0B2341] mb-6 flex items-center justify-center">
                  <span className="text-[#16A34A] font-heading text-6xl font-light">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#16A34A] mb-2">{d.role}</div>
                <h3 className="font-heading text-xl font-semibold text-[#0B2341] mb-3">{d.name}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{d.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="Engage with our leadership"
        title="Let's build India's renewable backbone — together."
        body="Whether you represent a DISCOM, a PSU, an industrial offtaker or hold suitable land, our team is ready to engage."
      />
    </div>
  );
}
